import { ArrowRight } from "lucide-react";
import { AddrLink, EmptyState, SkeletonRows, StatusBadge, TxLink, TypeBadge, ErrorState } from "@/components/common";
import { fmtEth, timeAgo, txAction, weiToEth } from "@/lib/format";

const DirBadge = ({ tx, self }) => {
  const from = (tx.from?.hash || "").toLowerCase();
  const to = (tx.to?.hash || tx.created_contract?.hash || "").toLowerCase();
  const me = self.toLowerCase();
  const dir = from === me && to === me ? "SELF" : from === me ? "OUT" : "IN";
  return (
    <span data-testid="direction-badge" className={`inline-flex text-[10px] font-semibold rounded-md px-1.5 py-0.5 ${
      dir === "OUT" ? "bg-yellow-500/15 text-yellow-500" : dir === "IN" ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"
    }`}>{dir}</span>
  );
};

export default function TxTable({ items, loading, error, compact = false, self }) {
  if (loading) return <SkeletonRows n={compact ? 5 : 10} />;
  if (error) return <ErrorState />;
  if (!items?.length)
    return <EmptyState title="No recent activity found" subtitle="Robinhood Chain is still new — this section will fill up as network activity grows." />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
            <th className="px-4 py-2.5 font-medium">Tx Hash</th>
            <th className="px-4 py-2.5 font-medium">Action</th>
            <th className="px-4 py-2.5 font-medium">From</th>
            {self && <th className="px-4 py-2.5 font-medium"></th>}
            <th className="px-4 py-2.5 font-medium">To</th>
            <th className="px-4 py-2.5 font-medium text-right">Value</th>
            {!compact && <th className="px-4 py-2.5 font-medium text-right">Fee</th>}
            <th className="px-4 py-2.5 font-medium text-right">Age</th>
            <th className="px-4 py-2.5 font-medium text-right">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((tx) => (
            <tr key={tx.hash} className="table-row-hover border-b last:border-0" data-testid="tx-row">
              <td className="px-4 py-3"><TxLink hash={tx.hash} chars={6} /></td>
              <td className="px-4 py-3"><TypeBadge>{txAction(tx)}</TypeBadge></td>
              <td className="px-4 py-3"><AddrLink hash={tx.from?.hash} name={tx.from?.name} /></td>
              {self && <td className="px-2 py-3"><DirBadge tx={tx} self={self} /></td>}
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1.5">
                  <ArrowRight size={11} className="text-muted-foreground" />
                  <AddrLink hash={tx.to?.hash || tx.created_contract?.hash} name={tx.to?.name} />
                </span>
              </td>
              <td className="px-4 py-3 text-right font-mono text-xs">
                {weiToEth(tx.value) > 0 ? fmtEth(tx.value, 5) : "—"}
              </td>
              {!compact && (
                <td className="px-4 py-3 text-right font-mono text-xs text-muted-foreground">
                  {tx.fee?.value ? fmtEth(tx.fee.value, 8) : "—"}
                </td>
              )}
              <td className="px-4 py-3 text-right text-xs text-muted-foreground whitespace-nowrap">{timeAgo(tx.timestamp)}</td>
              <td className="px-4 py-3 text-right"><StatusBadge status={tx.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
